/**
 * Event — which event this door phone has selected. Pure state: the
 * available-events list an operator picks from (populated by event.sync.ts)
 * and the selected event id, persisted per device (`.local()`) so a door
 * phone remembers its event across a reload — re-picking before every scan
 * session is an operational cost this device shouldn't pay twice. The
 * selected event id scopes verdict.sync.ts's own qrToken lookup (the query
 * the Ticket rule's fixed-depth match path authorises) — app state, never
 * enforced server-side: nothing server-side stops a misconfigured scanner
 * from reading a different event's own tickets subcollection if selected.
 *
 * `eventDetailsById` carries the SAME documents `eventOptions` above already
 * reduces to `{id, name, status}` — event.sync.ts's one listener parses each
 * doc once and writes both, so a consumer needing the full entity (schedule,
 * locationId, templateVariant — manual-lookup/order-detail.sync.ts's own
 * reconciler for `<order-detail>`, @momo/views/orders) pays no second read.
 *
 * `venueTimezone` is the one fact about that event this file holds rather
 * than reads off the document: the zone its naive wall-clock schedule means
 * anything in. It cannot be resolved here — `resolveEffectiveTimezone` needs
 * the venue documents `locationId` points at, and order-detail.sync.ts
 * already declined to open a locations read at the door for a smaller prize
 * than this one — so the cell carries the schema's own default as its floor
 * and whichever app mounts this door refines it from a catalog it already
 * holds.
 */
import { computed, state } from "@mhmo91/schmancy/state";
import { type Event, Location } from "@momo/types";

export type EventOption = {
	readonly id: string;
	readonly name: string;
	readonly status: "draft" | "published" | "archived";
};

// keepOnClear: the workspace's event list is public and identity-free
// (event.entity.ts: `allow read/list: if true`) — a sign-in on this phone
// must not wipe it. Its sole writer is the live query event.sync.ts opens
// once at module scope, and an unchanged query never re-emits, so a
// clearStates() reset strands it unanswered with nothing left to repopulate it:
// event-pick's Connecting… frame, forever, until the operator reloads.
//
// `null` until the list has answered for this organization — a switch returns
// it there — so an empty list always means the organization has no events.
export const eventOptions = state<readonly EventOption[] | null>(
	"scanner/event-options",
).memory(null, { keepOnClear: true });

// Explicit type-arg form (schmancy state.md's ScalarAPI example) — a nullable
// scalar's initial `null` literal needs the type named here, not inferred
// from a typed const.
//
// keepOnClear for settings.state.ts's own reason, applied to the pick those
// settings qualify: which event this entrance admits for is a property of the
// door position — one check-in account IS one door phone — not of whoever is
// standing at it. A shift change swaps the operator, never the door's event,
// and `.local()` promises this device re-picks nothing it already knows.
export const selectedEventId = state<string | null>(
	"scanner/selected-event-id",
).local(null, { keepOnClear: true });

export const selectedEvent = computed(
	() => eventOptions.value?.find((e) => e.id === selectedEventId.value) ?? null,
);

// keepOnClear for the same reason as eventOptions above — same listener, same
// public/identity-free doc, same "nothing left to repopulate it" risk on a
// clearStates() reset.
export const eventDetailsById = state<ReadonlyMap<string, Event.Event>>(
	"scanner/event-details",
).memory(new Map(), { keepOnClear: true });

// The IANA zone this door reads its venue's wall-clock facts in. One reader
// today: settings.state.ts turns it, with today's date, into the ticket types
// this lane admits before an operator narrows it — so a door in the wrong zone
// admits the wrong day's types for up to a day either side of midnight.
//
// The floor is LocationSchema's own default rather than a literal, the same
// forward-only fallback resolveEffectiveTimezone lands on when a locationId
// resolves to nothing (ticket-type.entity.ts) — one answer for an unresolvable
// venue, wherever the question is asked. keepOnClear for eventDetailsById's own
// reason: whatever writes this is a module-scope reconciler that need not
// re-emit, so a clearStates() reset would strand the door on the floor for the
// rest of the session.
export const venueTimezone = state("scanner/venue-timezone").memory(
	Location.LocationSchema.shape.timezone.parse(undefined),
	{ keepOnClear: true },
);
